import { useEffect, useState, useMemo, useRef, type ComponentType } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Shield, Bike, Scooter, AlertCircle, HelpCircle, Phone, Search, X } from 'lucide-react';
import FaqCard from "@/components/shared/faq-card";
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import useBrandName from '@/hooks/use-brand-name';
import { HOST_MB } from '@/lib/constants';
import { useContractsData } from '@/hooks/api/use-contracts';
import { getFaqByCategory, useFaq } from '@/hooks/api/use-faq';
import { hasContractTrottinette, hasContractVAE, hasOtherContract as hasOtherContractFn } from '@/lib/contracts';
import type { Faq } from '@/types/api/faq';

interface FaqSectionProps {
    id: string;
    icon: ComponentType<{ className?: string }>;
    title: string;
    items: Faq[];
}

const FaqSection = ({ id, icon: Icon, title, items }: FaqSectionProps) => {
    if (items.length === 0) return null;

    return (
        <Card id={id}>
            <CardHeader className="pb-6 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                    <Icon className="w-5 h-5 text-primary" />
                    {title}
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
                <div className="space-y-3">
                    {items.map((item, index) => (
                        <FaqCard faq={item} key={index} />
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

const filterFaqs = (items: Faq[], query: string) => {
    if (!query) return items;
    const lower = query.toLowerCase();
    return items.filter(faq =>
        faq.question?.toLowerCase().includes(lower) ||
        faq.content_html?.toLowerCase().includes(lower)
    );
};

interface ListeQuestionsContentProps {
    faqs: Faq[];
}

const ListeQuestionsContent = ({ faqs }: ListeQuestionsContentProps) => {
    const brandName = useBrandName();
    const contracts = useContractsData();
    const hasVAE = hasContractVAE(contracts);
    const hasTrottinette = hasContractTrottinette(contracts);
    const hasOtherContract = hasOtherContractFn(contracts);
    const { contract, accident, other, documents, trottinette_faq, vae_faq } = useMemo(() => ({
        contract: getFaqByCategory(faqs, 1),
        accident: getFaqByCategory(faqs, 2),
        other: getFaqByCategory(faqs, 3),
        documents: getFaqByCategory(faqs, 4),
        trottinette_faq: getFaqByCategory(faqs, 5),
        vae_faq: getFaqByCategory(faqs, 6),
    }), [faqs]);
    const [searchQuery, setSearchQuery] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    const searchParams = new URLSearchParams(window.location.search);
    const contact = searchParams.get('contact');

    let telephone = "0255453458";
    if (contact === "gestion") {
        telephone = "0247510707";
    } else if (contact === "souscription") {
        telephone = "0255453458";
    }

    const filteredSections = useMemo(() => ({
        documents: filterFaqs(documents, searchQuery),
        vae_faq: filterFaqs(vae_faq, searchQuery),
        trottinette_faq: filterFaqs(trottinette_faq, searchQuery),
        contract: filterFaqs(contract, searchQuery),
        accident: filterFaqs(accident, searchQuery),
        other: filterFaqs(other, searchQuery),
    }), [searchQuery, documents, vae_faq, trottinette_faq, contract, accident, other]);

    const totalResults = Object.values(filteredSections).reduce((sum, items) => sum + items.length, 0);

    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            const id = hash.replace('#', '');
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
            } else {
                const interval = setInterval(() => {
                    const delayedEl = document.getElementById(id);
                    if (delayedEl) {
                        delayedEl.scrollIntoView({ behavior: 'smooth' });
                        clearInterval(interval);
                    }
                }, 300);
                setTimeout(() => clearInterval(interval), 3000);
            }
        }
    }, []);

    return (
        <div className="space-y-6 mt-6">
            <p className="text-lg text-muted-foreground">
                Vous avez une question sur <span className="font-semibold text-foreground">votre contrat d'assurance ?</span>
            </p>

            {/* Barre de recherche */}
            <div data-tutorial="questions-search" className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                <input
                    ref={searchRef}
                    type="text"
                    placeholder="Rechercher une question..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-lg border border-border bg-card py-4 pl-12 pr-10 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                />
                {searchQuery && (
                    <button
                        onClick={() => { setSearchQuery(''); searchRef.current?.focus(); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted transition-colors cursor-pointer"
                        aria-label="Effacer la recherche"
                    >
                        <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                )}
            </div>

            {searchQuery && (
                <p className="text-sm text-muted-foreground">
                    {totalResults} {totalResults > 1 ? 'résultats' : 'résultat'} pour « <span className="font-medium text-foreground">{searchQuery}</span> »
                </p>
            )}

            {contact && HOST_MB === 43397 && (
                <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="pt-6 text-center">
                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mx-auto mb-4">
                            <Phone className="w-6 h-6 text-primary" />
                        </div>
                        <p className="font-semibold mb-4">{brandName} est à votre écoute en toute confiance</p>
                        <Button asChild className="cursor-pointer text-white">
                            <a href={`tel:${telephone}`}>
                                <Phone className="w-4 h-4" />
                                {telephone.replace(/(\d{2})(?=\d)/g, '$1 ')}
                            </a>
                        </Button>
                    </CardContent>
                </Card>
            )}

            {hasOtherContract && (
                <FaqSection id="documents" icon={FileText} title="Mes documents" items={filteredSections.documents} />
            )}

            {hasVAE && (
                <FaqSection id="contrat_vae" icon={Bike} title="Mon contrat VAE" items={filteredSections.vae_faq} />
            )}

            {hasTrottinette && (
                <FaqSection id="contrat_trottinette" icon={Scooter} title="Mon contrat Trottinette" items={filteredSections.trottinette_faq} />
            )}

            {hasOtherContract && (
                <>
                    <FaqSection id="contrat" icon={Shield} title="Mon contrat" items={filteredSections.contract} />
                    <FaqSection id="sinistre" icon={AlertCircle} title="Mes sinistres" items={filteredSections.accident} />
                    <FaqSection id="autres_questions" icon={HelpCircle} title="Mes autres questions" items={filteredSections.other} />
                </>
            )}

            {searchQuery && totalResults === 0 && (
                <Card className="border-dashed">
                    <CardContent className="pt-6 text-center">
                        <p className="text-muted-foreground">Aucun résultat pour votre recherche.</p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

/** Liste des questions de la FAQ, relue à chaque affichage. */
const ListeQuestions = () => {
    const faqQuery = useFaq();

    if (faqQuery.isError) return <p>Erreur dans le chargement</p>;
    if (faqQuery.isPending) return <SmallSpinner />;

    return <ListeQuestionsContent faqs={faqQuery.data ?? []} />;
};

export default ListeQuestions;
